import React, { useState } from 'react';
import { useTheme, createThemedStyles } from '../../contexts/ThemeContext';
import type { Theme } from '../../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faCheck, faCheckCircle } from '@/icons';
import { MonetaryInput } from '../MonetaryInput';
import { CustomToggle } from '../CustomToggle';
import { CustomTooltip } from '../CustomTooltip';
import type { HealthCalculatorInitialData, HealthCalculatorProps } from './types';
import type { HealthCalculatorSectionModel } from '@/types/case';

type PremiumSection = { employeeOnly: string; option: 'child' | 'family'; employeeAndChild: string; employeeAndFamily: string };

function toPremiumSection(m?: HealthCalculatorSectionModel): PremiumSection {
    if (!m) return { employeeOnly: '', option: 'child', employeeAndChild: '', employeeAndFamily: '' };
    return {
        employeeOnly: m.employeeOnly != null ? String(m.employeeOnly) : '',
        option: m.option === 'family' ? 'family' : 'child',
        employeeAndChild: m.employeeAndChild != null ? String(m.employeeAndChild) : '',
        employeeAndFamily: m.employeeAndFamily != null ? String(m.employeeAndFamily) : '',
    };
}

function toSectionModel(s: PremiumSection, monthlyFactor: number): HealthCalculatorSectionModel {
    const emp = (parseFloat(s.employeeOnly) || 0) * monthlyFactor;
    const child = (parseFloat(s.employeeAndChild) || 0) * monthlyFactor;
    const family = (parseFloat(s.employeeAndFamily) || 0) * monthlyFactor;
    return {
        employeeOnly: Math.round(emp * 100) / 100,
        option: s.option,
        employeeAndChild: Math.round(child * 100) / 100,
        employeeAndFamily: Math.round(family * 100) / 100,
    };
}

export function calculateHealthInsuranceMonthlyTotal(
    health: HealthCalculatorSectionModel | undefined,
    dental: HealthCalculatorSectionModel | undefined,
    vision: HealthCalculatorSectionModel | undefined,
    isHealthIncluded: boolean,
    isDentalIncluded: boolean,
    isVisionIncluded: boolean,
    proRateFactor: number
): number {
    const sectionSubtotal = (s: HealthCalculatorSectionModel): number => {
        const emp = s.employeeOnly ?? 0;
        const selected = s.option === 'family' ? (s.employeeAndFamily ?? 0) : (s.employeeAndChild ?? 0);
        return Math.round((selected - emp) * 100) / 100;
    };
    const sectionTotal = (s: HealthCalculatorSectionModel | undefined, included: boolean): number => {
        if (!included || !s) return 0;
        return Math.round(sectionSubtotal(s) * proRateFactor * 100) / 100;
    };
    return Math.round((
        sectionTotal(health, isHealthIncluded) +
        sectionTotal(dental, isDentalIncluded) +
        sectionTotal(vision, isVisionIncluded)
    ) * 100) / 100;
}

const INPUT_COLUMN_WIDTH = 140;

function HealthPremiumStep({
    section,
    setSection,
    theme,
    decimalPlaces,
    monthlyFactor,
    proRateFactor,
    tabLabel,
}: {
    section: PremiumSection;
    setSection: React.Dispatch<React.SetStateAction<PremiumSection>>;
    theme: Theme;
    decimalPlaces: number;
    monthlyFactor: number;
    proRateFactor: number;
    tabLabel: string;
}) {
    const emp = parseFloat(section.employeeOnly) || 0;
    const other = section.option === 'child' ? (parseFloat(section.employeeAndChild) || 0) : (parseFloat(section.employeeAndFamily) || 0);
    const subtotal = Math.round((other - emp) * 100) / 100;
    const monthlySubtotal = Math.round(subtotal * monthlyFactor * proRateFactor * 100) / 100;
    const labelStyle = {
        display: 'block' as const,
        fontSize: theme.typography.fontSize.sm,
        fontWeight: theme.typography.fontWeight.medium,
        color: theme.colors.text.primary,
        marginBottom: theme.spacing.xs,
    };
    const rowBase = {
        display: 'flex' as const,
        alignItems: 'center' as const,
        gap: theme.spacing.sm,
        padding: theme.spacing.sm,
    };
    return (
        <div style={{ marginBottom: theme.spacing.lg, marginTop: theme.spacing.md }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm, marginBottom: theme.spacing.md }}>
                <div
                    style={{
                        ...rowBase,
                        border: `1px solid ${theme.colors.border.default}`,
                        borderRadius: theme.borderRadius.sm,
                        backgroundColor: theme.colors.surface,
                    }}
                >
                    <div style={{ width: 18, flexShrink: 0 }} aria-hidden />
                    <label style={{ ...labelStyle, marginBottom: 0, flex: 1 }}>Employee Only</label>
                    <div style={{ maxWidth: INPUT_COLUMN_WIDTH }}>
                        <MonetaryInput
                            calculatorType="none"
                            unit="none"
                            value={parseFloat(section.employeeOnly) || 0}
                            onChange={(v) => {
                                const raw = v === 0 ? '' : String(v);
                                setSection((s) => ({
                                    ...s,
                                    employeeOnly: raw,
                                    ...(raw === '' || v === 0 ? { employeeAndChild: '', employeeAndFamily: '', option: 'child' as const } : {}),
                                }));
                            }}
                            decimalPlaces={decimalPlaces}
                            placeholder="0.00"
                            style={{ width: '100%' }}
                        />
                    </div>
                </div>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, margin: 0 }}>Enter exactly one of the following:</p>
                {(
                    [
                        { key: 'child' as const, label: 'Employee + Child(ren)', value: section.employeeAndChild, setValue: (v: string) => setSection((s) => ({ ...s, option: 'child', employeeAndChild: v, employeeAndFamily: '' })) },
                        { key: 'family' as const, label: 'Employee + Family', value: section.employeeAndFamily, setValue: (v: string) => setSection((s) => ({ ...s, option: 'family', employeeAndFamily: v, employeeAndChild: '' })) },
                    ] as const
                ).map(({ key, label, value, setValue }) => {
                    const isSelected = section.option === key;
                    return (
                        <div
                            key={key}
                            role="button"
                            tabIndex={0}
                            onClick={() => setSection((s) => ({ ...s, option: key, employeeAndChild: key === 'child' ? s.employeeAndChild : '', employeeAndFamily: key === 'family' ? s.employeeAndFamily : '' }))}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSection((s) => ({ ...s, option: key, employeeAndChild: key === 'child' ? s.employeeAndChild : '', employeeAndFamily: key === 'family' ? s.employeeAndFamily : '' })); } }}
                            style={{
                                ...rowBase,
                                border: `1px solid ${isSelected ? theme.colors.primary : theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.sm,
                                backgroundColor: isSelected ? `${theme.colors.primary}08` : theme.colors.surface,
                                cursor: 'pointer',
                                transition: `border-color ${theme.transitions?.normal ?? '0.2s'}, background-color ${theme.transitions?.normal ?? '0.2s'}`,
                            }}
                        >
                            <div
                                style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: '50%',
                                    border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.border.default}`,
                                    backgroundColor: isSelected ? theme.colors.primary : 'transparent',
                                    flexShrink: 0,
                                    boxSizing: 'border-box',
                                }}
                                aria-hidden
                            />
                            <label style={{ ...labelStyle, marginBottom: 0, flex: 1, cursor: 'pointer' }}>{label}</label>
                            <div onClick={(e) => e.stopPropagation()} style={{ maxWidth: INPUT_COLUMN_WIDTH }}>
                                <MonetaryInput
                                    calculatorType="none"
                                    unit="none"
                                    value={parseFloat(value) || 0}
                                    onChange={(v) => setValue(v === 0 ? '' : String(v))}
                                    decimalPlaces={decimalPlaces}
                                    placeholder="0.00"
                                    style={{ width: '100%' }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    <span>Total Per Pay Period</span>
                    <span>${subtotal.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.medium, color: theme.colors.text.primary }}>
                    <span>{tabLabel} Monthly Subtotal</span>
                    <span>${monthlySubtotal.toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}</span>
                </div>
            </div>
        </div>
    );
}

type TabKey = 'health' | 'dental' | 'vision';
type PayPeriod = 'weekly' | 'biweekly' | 'semimonthly' | 'monthly';

const TABS: { key: TabKey; label: string }[] = [
    { key: 'health', label: 'Health' },
    { key: 'dental', label: 'Dental' },
    { key: 'vision', label: 'Vision' },
];

const PAY_PERIOD_OPTIONS: { value: PayPeriod; label: string }[] = [
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Every 2 Weeks' },
    { value: 'semimonthly', label: 'Twice Monthly' },
    { value: 'monthly', label: 'Monthly' },
];

function payPeriodToMonthlyFactor(period: PayPeriod): number {
    const periodsPerYear = period === 'weekly' ? 52 : period === 'biweekly' ? 26 : period === 'semimonthly' ? 24 : 12;
    return periodsPerYear / 12;
}

function sectionIncluded(section: PremiumSection): boolean {
    return (parseFloat(section.employeeOnly) || 0) !== 0 ||
        (parseFloat(section.employeeAndChild) || 0) !== 0 ||
        (parseFloat(section.employeeAndFamily) || 0) !== 0;
}

function hasInitialData(data?: HealthCalculatorInitialData): boolean {
    const section = (s: HealthCalculatorSectionModel | undefined) => (s?.employeeOnly ?? 0) !== 0 || (s?.employeeAndChild ?? 0) !== 0 || (s?.employeeAndFamily ?? 0) !== 0;
    return section(data?.health) || section(data?.dental) || section(data?.vision);
}

export const HealthCalculator: React.FC<HealthCalculatorProps> = ({
    decimalPlaces,
    onSave,
    onClose,
    initialData,
}) => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const childrenCount = initialData?.childrenCount ?? 0;
    const [activeTab, setActiveTab] = useState<TabKey>('health');
    const [payPeriod, setPayPeriod] = useState<PayPeriod>(() => (initialData && hasInitialData(initialData) ? 'monthly' : 'weekly'));
    const [healthPremiums, setHealthPremiums] = useState<PremiumSection>(() => toPremiumSection(initialData?.health));
    const [dentalPremiums, setDentalPremiums] = useState<PremiumSection>(() => toPremiumSection(initialData?.dental));
    const [visionPremiums, setVisionPremiums] = useState<PremiumSection>(() => toPremiumSection(initialData?.vision));
    const [isProrated, setIsProrated] = useState<boolean>(initialData?.isProrated ?? false);
    const [proratedChildrenCount, setProratedChildrenCount] = useState<string>(
        String(initialData?.proratedChildrenCount ?? (childrenCount > 0 ? childrenCount : 1))
    );

    const healthSubtotal = (section: PremiumSection): number => {
        const emp = parseFloat(section.employeeOnly) || 0;
        const other = section.option === 'child' ? (parseFloat(section.employeeAndChild) || 0) : (parseFloat(section.employeeAndFamily) || 0);
        return Math.round((other - emp) * 100) / 100;
    };
    const minPlanCount = Math.max(1, childrenCount);
    const proRateFactor = (): number => {
        if (!isProrated) return 1;
        const planCount = Math.max(minPlanCount, parseInt(proratedChildrenCount) || minPlanCount);
        return childrenCount / planCount;
    };
    const sectionMonthlyTotal = (section: PremiumSection): number => {
        const factor = payPeriodToMonthlyFactor(payPeriod);
        return Math.round(healthSubtotal(section) * factor * proRateFactor() * 100) / 100;
    };
    const monthlyTotal = (): number => {
        let t = 0;
        if (sectionIncluded(healthPremiums)) t += sectionMonthlyTotal(healthPremiums);
        if (sectionIncluded(dentalPremiums)) t += sectionMonthlyTotal(dentalPremiums);
        if (sectionIncluded(visionPremiums)) t += sectionMonthlyTotal(visionPremiums);
        return Math.round(t * 100) / 100;
    };

    const sectionValid = (section: PremiumSection): boolean => {
        const other = section.option === 'child' ? (parseFloat(section.employeeAndChild) || 0) : (parseFloat(section.employeeAndFamily) || 0);
        return (parseFloat(section.employeeOnly) || 0) >= 0 && other >= 0;
    };
    const prorateChildrenInvalid = isProrated && childrenCount > 0 && (parseInt(proratedChildrenCount) || 0) < childrenCount;

    const canApply = (): boolean => {
        const hasAny = sectionIncluded(healthPremiums) || sectionIncluded(dentalPremiums) || sectionIncluded(visionPremiums);
        if (!hasAny) return false;
        if (sectionIncluded(healthPremiums) && !sectionValid(healthPremiums)) return false;
        if (sectionIncluded(dentalPremiums) && !sectionValid(dentalPremiums)) return false;
        if (sectionIncluded(visionPremiums) && !sectionValid(visionPremiums)) return false;
        if (prorateChildrenInvalid) return false;
        return true;
    };

    const handleApply = () => {
        const factor = payPeriodToMonthlyFactor(payPeriod);
        const rounded = Math.round(monthlyTotal() * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
        const payload = {
            isHealthIncluded: sectionIncluded(healthPremiums),
            isDentalIncluded: sectionIncluded(dentalPremiums),
            isVisionIncluded: sectionIncluded(visionPremiums),
            health: toSectionModel(healthPremiums, factor),
            dental: toSectionModel(dentalPremiums, factor),
            vision: toSectionModel(visionPremiums, factor),
            isProrated,
            proratedChildrenCount: isProrated ? Math.max(minPlanCount, parseInt(proratedChildrenCount) || minPlanCount) : null,
        };
        onSave(rounded, payload);
        onClose();
    };

    return (
        <>
            <div style={{ display: 'flex', gap: 0, border: `1px solid ${theme.colors.border.default}`, borderRadius: theme.borderRadius.sm, padding: 2, backgroundColor: theme.colors.surface, marginBottom: theme.spacing.lg }}>
                {TABS.map(({ key, label }) => {
                    const isActive = activeTab === key;
                    return (
                        <button
                            key={key}
                            type="button"
                            onClick={() => setActiveTab(key)}
                            style={{
                                flex: 1,
                                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                                border: 'none',
                                borderRadius: isActive ? theme.borderRadius.sm : 0,
                                backgroundColor: isActive ? theme.colors.primary : 'transparent',
                                color: isActive ? theme.colors.text.inverse : theme.colors.text.primary,
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: isActive ? theme.typography.fontWeight.medium : theme.typography.fontWeight.normal,
                                cursor: 'pointer',
                                transition: `background-color ${theme.transitions?.normal ?? '0.2s'}, color ${theme.transitions?.normal ?? '0.2s'}`,
                            }}
                        >
                            {label}
                        </button>
                    );
                })}
            </div>

            <div style={{ marginBottom: theme.spacing.lg }}>
                <label style={{ display: 'block', fontSize: theme.typography.fontSize.sm, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, marginBottom: theme.spacing.sm }}>Select Applicable Pay Period:</label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: theme.spacing.sm, marginBottom: theme.spacing.sm }}>
                    {PAY_PERIOD_OPTIONS.map((opt) => {
                        const isSelected = payPeriod === opt.value;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => setPayPeriod(opt.value)}
                                style={{
                                    padding: theme.spacing.sm,
                                    border: `2px solid ${theme.colors.primary}`,
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    cursor: 'pointer',
                                    backgroundColor: theme.colors.primary,
                                    color: theme.colors.text.inverse,
                                    opacity: isSelected ? 1 : 0.8,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: theme.spacing.sm,
                                }}
                            >
                                {opt.label}
                                {isSelected && <FontAwesomeIcon icon={faCheckCircle} style={{ color: theme.colors.status.success }} />}
                            </button>
                        );
                    })}
                </div>
                <p style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, margin: 0 }}>Enter premium amounts per pay period below.</p>
            </div>

            <div style={{ minHeight: 120, marginBottom: theme.spacing.lg }}>
                {activeTab === 'health' && <HealthPremiumStep section={healthPremiums} setSection={setHealthPremiums} theme={theme} decimalPlaces={decimalPlaces} monthlyFactor={payPeriodToMonthlyFactor(payPeriod)} proRateFactor={proRateFactor()} tabLabel="Health" />}
                {activeTab === 'dental' && <HealthPremiumStep section={dentalPremiums} setSection={setDentalPremiums} theme={theme} decimalPlaces={decimalPlaces} monthlyFactor={payPeriodToMonthlyFactor(payPeriod)} proRateFactor={proRateFactor()} tabLabel="Dental" />}
                {activeTab === 'vision' && <HealthPremiumStep section={visionPremiums} setSection={setVisionPremiums} theme={theme} decimalPlaces={decimalPlaces} monthlyFactor={payPeriodToMonthlyFactor(payPeriod)} proRateFactor={proRateFactor()} tabLabel="Vision" />}
            </div>

            <div style={{ paddingTop: theme.spacing.lg, borderTop: `1px solid ${theme.colors.border.default}`, marginBottom: theme.spacing.sm }}>
                <CustomToggle
                    checked={isProrated}
                    onChange={(checked) => {
                        setIsProrated(checked);
                        if (checked && (!proratedChildrenCount || parseInt(proratedChildrenCount) < minPlanCount)) {
                            setProratedChildrenCount(String(minPlanCount));
                        }
                    }}
                    label="Pro-rate"
                />
                <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginTop: theme.spacing.sm, paddingLeft: theme.spacing.sm, opacity: isProrated ? 1 : 0.4 }}>
                    <label style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary, whiteSpace: 'nowrap' }}>
                        Total children on plan:
                    </label>
                    <CustomTooltip text={`Count must be at least ${childrenCount}`} show={prorateChildrenInvalid} position="top" portal>
                        <input
                            type="number"
                            min={minPlanCount}
                            disabled={!isProrated}
                            value={proratedChildrenCount}
                            onChange={(e) => setProratedChildrenCount(e.target.value)}
                            style={{
                                width: 64,
                                padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                border: `1px solid ${prorateChildrenInvalid ? theme.colors.status.error : theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.sm,
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.primary,
                                backgroundColor: theme.colors.surface,
                                textAlign: 'center',
                                cursor: isProrated ? 'text' : 'not-allowed',
                            }}
                        />
                    </CustomTooltip>
                    {childrenCount > 0 && (
                        <span style={{ fontSize: theme.typography.fontSize.sm, color: theme.colors.text.secondary }}>
                            (Including the {childrenCount} child(ren) on this case)
                        </span>
                    )}
                </div>
            </div>

            <div style={{ marginTop: theme.spacing.sm, marginBottom: theme.spacing.lg }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: theme.typography.fontSize.md, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary }}>
                    <span>Monthly total</span>
                    <span>${monthlyTotal().toLocaleString('en-US', { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces })}</span>
                </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <button onClick={onClose} style={{ ...themedStyles.button.primary, backgroundColor: 'transparent', color: theme.colors.text.primary, border: `1px solid ${theme.colors.border.default}`, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                    <FontAwesomeIcon icon={faTimes} /> Cancel
                </button>
                <button onClick={handleApply} disabled={!canApply()} style={{ ...themedStyles.button.primary, backgroundColor: theme.colors.primary, color: theme.colors.text.inverse, padding: `${theme.spacing.sm} ${theme.spacing.lg}`, display: 'flex', alignItems: 'center', gap: theme.spacing.sm, cursor: canApply() ? 'pointer' : 'not-allowed' }}>
                    <FontAwesomeIcon icon={faCheck} /> Apply
                </button>
            </div>
        </>
    );
};
